import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";

import { Stack, Typography } from "@mui/material";

import FormControlLabel from "@/components/atoms/form-label/label";
import { Option, Select } from "@/components/atoms/select-dropdown/select";
import TextField from "@/components/atoms/text-field/text-field";
import {
  canSkipPrefilledGroup,
  getPrefilledData,
  usePrefilledStatus,
} from "@/helpers/prefill-hook";

type Props = {
  data?: string | string[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  succeededStep: number;
  setSucceededStep: (step: number) => void;
  locale: string;
  brand: string | string[] | undefined;
  country: string | string[] | undefined;
  page: "unauthorized" | "stolen" | "incorrect";
};

let isInitialPrefilled = true;

function CustomerDetailsStep(props: Readonly<Props>) {
  const {
    data,
    setActiveStep,
    activeStep,
    brand,
    page,
    country,
    setSucceededStep,
  } = props;
  const { t } = useTranslation();
  const isDevelopment =
    process.env.NODE_ENV === "development" ||
    (typeof window !== "undefined" &&
      window.location.href.indexOf("localhost") !== -1);

  const [numberType, setNumberType] = useState("");
  const [email, setEmail] = useState("");
  const [emailRepeat, setEmailRepeat] = useState("");
  usePrefilledStatus("customer__number-type", data, (val) => {
    if (typeof val === "string" && val?.length && !numberType.length) {
      setNumberType(val);
    }
  });
  usePrefilledStatus("customer-information[email]", data, (val) => {
    if (
      typeof val === "string" &&
      isDevelopment &&
      val.length &&
      !email.length
    ) {
      // no personal data in prod logs in clear text
      setEmail(val);
      setEmailRepeat(val);
    }
  });

  useEffect(() => {
    if (isInitialPrefilled) {
      if (
        isDevelopment &&
        canSkipPrefilledGroup("customer", data, 6) &&
        activeStep === 2
      ) {
        // no personal data in prod logs ergo not able to skip
        setActiveStep(3);
        setSucceededStep(3);
      }
      isInitialPrefilled = false;
    }
  }, [data, setActiveStep, activeStep, setSucceededStep, isDevelopment]);

  const isFinishMarket =
    (country && country == "fi") || (!country && props.locale == "fi");

  return (
    <>
      <Typography variant="body1">
        {t(
          "In order to process your claim as quickly as possible, please provide us with the necessary information."
        )}
      </Typography>
      <FormControlLabel
        labelPlacement="top"
        control={
          <Select
            name="customer__number-type"
            required={activeStep === 2}
            value={numberType}
            onChange={(e) =>
              setNumberType((e.target as HTMLSelectElement).value)
            }
          >
            <Option value="" hidden disabled>
              {t("Choose")}
            </Option>
            <Option value="contract-number">{t("Contract number")}</Option>
            {(brand === "eurocard" || brand === "airplus") &&
            isFinishMarket &&
            page === "stolen" ? null : (
              <Option value="account-number">{t("Account number")}</Option>
            )}
            <Option value="invoice">{t("Invoice number")}</Option>
          </Select>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Select number type you would like to provide us with?")}
          </Typography>
        }
      />
      <FormControlLabel
        labelPlacement="top"
        className={classNames({
          "no-display": numberType !== "contract-number",
        })}
        control={
          <TextField
            required={numberType === "contract-number" && activeStep === 2}
            autoComplete="off"
            defaultValue={
              isDevelopment
                ? getPrefilledData(
                    "customer-information[contract-number]",
                    data
                  )
                : ""
            }
            inputProps={{
              pattern: "[0-9]{9,}",
              title:
                t("The field must contain at least nine digits") ||
                "The field must contain at least nine digits",
            }}
            name="customer-information[contract-number]"
          />
        }
        label={
          <Typography variant="body1" component="span">
            {t("Contract number")}
          </Typography>
        }
      />
      <FormControlLabel
        labelPlacement="top"
        className={classNames({
          "no-display": numberType !== "account-number",
        })}
        control={
          <TextField
            required={numberType === "account-number" && activeStep === 2}
            defaultValue={
              isDevelopment
                ? getPrefilledData("customer-information[account-number]", data)
                : ""
            }
            autoComplete="off"
            inputProps={{
              pattern: "[0-9]{9,}",
              title:
                t("The field must contain at least nine digits") ||
                "The field must contain at least nine digits",
            }}
            name="customer-information[account-number]"
          />
        }
        label={
          <Typography variant="body1" component="span">
            {t("Account number")}
          </Typography>
        }
      />
      <FormControlLabel
        labelPlacement="top"
        className={classNames({ "no-display": numberType !== "invoice" })}
        control={
          <TextField
            defaultValue={
              isDevelopment
                ? getPrefilledData("customer-information[invoice-number]", data)
                : ""
            }
            required={numberType === "invoice" && activeStep === 2}
            autoComplete="off"
            name="customer-information[invoice-number]"
            inputProps={{
              pattern: "[0-9]{9,}",
              title:
                t("The field must contain at least nine digits") ||
                "The field must contain at least nine digits",
            }}
          />
        }
        label={
          <Typography variant="body1" component="span">
            {t("Invoice number")}
          </Typography>
        }
      />
      <Stack
        className={classNames({ "no-display": !numberType.length })}
        sx={{
          gap: 2,
        }}
      >
        <FormControlLabel
          labelPlacement="top"
          control={
            <TextField
              required={activeStep === 2}
              name="customer-information[first-name]"
              defaultValue={
                isDevelopment
                  ? getPrefilledData("customer-information[first-name]", data)
                  : ""
              }
            />
          }
          label={
            <Typography variant="body1" component="span">
              {t("First name")}
            </Typography>
          }
        />
        <FormControlLabel
          labelPlacement="top"
          control={
            <TextField
              required={activeStep === 2}
              name="customer-information[last-name]"
              defaultValue={
                isDevelopment
                  ? getPrefilledData("customer-information[last-name]", data)
                  : ""
              }
            />
          }
          label={
            <Typography variant="body1" component="span">
              {t("Last name")}
            </Typography>
          }
        />
        <FormControlLabel
          labelPlacement="top"
          control={
            <TextField
              required={activeStep === 2}
              name="customer-information[email]"
              inputProps={{
                pattern: ".*@[A-Za-z0-9\\.\\-]+\\.[A-Za-z]{2,}", // extends valid email with requiring a hostname with .se, .com, etc
                title:
                  t("You need to fill in a valid e-mail address") ||
                  "You need to fill in a valid e-mail address",
              }}
              type="email"
              value={email.toLocaleLowerCase()}
              onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
            />
          }
          label={
            <Typography variant="body1" component="span">
              {t("E-mail address")}
            </Typography>
          }
        />
        <FormControlLabel
          labelPlacement="top"
          control={
            <TextField
              required={activeStep === 2}
              type="email"
              value={emailRepeat.toLocaleLowerCase()}
              onChange={(e) =>
                setEmailRepeat(e.target.value.toLocaleLowerCase())
              }
              inputProps={{
                pattern: email,
                title:
                  t("Email addresses must be the same") ||
                  "Email addresses must be the same",
              }}
            />
          }
          label={
            <Typography variant="body1" component="span">
              {t("Verify your e-mail address")}
            </Typography>
          }
        />
        <FormControlLabel
          labelPlacement="top"
          control={
            <TextField
              required={activeStep === 2}
              name="customer-information[telephone-number]"
              defaultValue={
                isDevelopment
                  ? getPrefilledData(
                      "customer-information[telephone-number]",
                      data
                    )
                  : ""
              }
              type="tel"
              helperText={t(
                "Enter phone number with country code, e.g. +46 76 xxx xxxx"
              )}
            />
          }
          label={
            <Typography variant="body1" component="span">
              {t("Telephone number")}
            </Typography>
          }
        />
      </Stack>
    </>
  );
}

export default CustomerDetailsStep;
